import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import th from "./locales/th";
import en from "./locales/en";

export const SUPPORTED_LANGS = ["th", "en"] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

const STORAGE_KEY = "portal.lang";

/**
 * English is the app default (principal decision 2026-07-23). Thai is a first-class, fully-translated
 * SELECTABLE alternative — reached via the AppShell language switcher, never forced. This reads the
 * user's last choice back out of localStorage; anything else (unset, cleared storage, a corrupted value,
 * a stale language this build no longer ships) falls back to "en" rather than guessing.
 */
function initialLang(): Lang {
  if (typeof localStorage === "undefined") return "en";
  const stored = localStorage.getItem(STORAGE_KEY);
  return (SUPPORTED_LANGS as readonly string[]).includes(stored ?? "") ? (stored as Lang) : "en";
}

const initial = initialLang();

void i18n.use(initReactI18next).init({
  resources: {
    th: { translation: th },
    en: { translation: en },
  },
  lng: initial,
  fallbackLng: "en",
  // Single namespace only — disable the ':' namespace separator (keep the '.' key separator for nested
  // resources) so dynamic keys built from backend reason codes that contain a colon (e.g.
  // "zoom_error:502") resolve to a real leaf key instead of being parsed as namespace+key (which would
  // always miss and silently fall back to the generic copy).
  nsSeparator: false,
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
});

// index.html ships a static lang="en" (it has to — it's served before any JS runs); sync it to whatever
// initialLang() actually resolved so a returning Thai-selecting user doesn't get one paint frame (and every
// AT/font-shaping decision) tagged as English.
if (typeof document !== "undefined") document.documentElement.lang = initial;

/**
 * Switches the active language: persists the choice for next visit, tells i18next to re-render every
 * `t()` consumer, and keeps `<html lang>` in sync (screen readers + the browser's own Thai/Latin
 * hyphenation and font-shaping heuristics key off it, not off i18next's internal state).
 */
export function setLang(lang: Lang): void {
  void i18n.changeLanguage(lang);
  if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, lang);
  if (typeof document !== "undefined") document.documentElement.lang = lang;
}

export default i18n;
